import { Soldier } from './entities/Soldier.js';
import { TeamBase } from './entities/TeamBase.js';
import { Metal } from './entities/Metal.js';
import { Corpse } from './entities/Corpse.js';
import { Nebula } from './entities/Nebula.js';
import { Crystal } from './entities/Crystal.js';
import { InputManager } from './systems/InputManager.js';
import { Renderer } from './systems/Renderer.js';
import { UIManager } from './systems/UIManager.js';
import { RendererFactory } from './renderers/RendererFactory.js';
import { CollisionSystem } from './systems/CollisionSystem.js';
import { TEAM_CONFIG, GAME_CONFIG, BASE_CONFIG, RESOURCE_CONFIG, UI_CONFIG, CANVAS_CONFIG } from './config.js';
import { audioManager } from './systems/AudioManager.js';
import { VERSION } from './version.js';
import { setDebugMode } from './utils/debug.js';
import { PWAInstaller } from './pwa-installer.js';
import { configWindow } from './systems/ConfigWindow.js';
import { statsWindow } from './systems/StatsWindow.js';
import { versionNotesWindow } from './systems/VersionNotesWindow.js';
import { soulforgeAPI } from './systems/SoulforgeAPI.js';
import { BaseWindow } from './systems/BaseWindow.js';
import { isDebugMode, debugLog, debugError, debugWarn } from './utils/debug.js';

/**
 * Game - Main game class and game loop
 *
 * Responsibilities:
 * - Initialize game systems (rendering, collision, input, UI, audio)
 * - Manage game state (entities, teams, win conditions)
 * - Run main update/render loop at 60 FPS
 * - Handle spawning (units, resources, bases)
 * - Track team statistics and win conditions
 * - Dual renderer support (2D Canvas / 3D Three.js)
 *
 * Architecture:
 * - Entity arrays: soldiers, projectiles, resources, bases, corpses
 * - System objects: renderer, collisionSystem, inputManager, uiManager
 * - Event-driven: callbacks for kills, corpses, win conditions
 *
 * Game Loop (60 FPS):
 * 1. Update all entities (AI, movement, effects)
 * 2. Check collisions (projectiles, corpses, resources)
 * 3. Update UI (stats, team info)
 * 4. Render all entities
 * 5. Check win conditions
 */
class Game {
  constructor() {
    // Two canvases for different renderer types
    this.canvas2D = document.getElementById('gameCanvas');
    this.canvas3D = document.getElementById('gameCanvas3D');
    
    // Start with 2D canvas
    this.canvas = this.canvas2D;
    
    // Set initial canvas sizes using visualViewport if available
    const width = window.visualViewport ? window.visualViewport.width : window.innerWidth;
    const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    this.canvas2D.width = this.canvas3D.width = width;
    this.canvas2D.height = this.canvas3D.height = height;

    // Game state
    this.soldiers = [];
    this.projectiles = [];
    this.resources = [];
    this.bases = [];
    this.currentTeam = TEAM_CONFIG.DEFAULT_TEAM;
    this.running = true;
    this.paused = false;
    this.gameStartTime = Date.now();
    
    // Make game time globally accessible for logging
    window.getGameTime = () => {
      const elapsed = Date.now() - this.gameStartTime;
      const seconds = Math.floor(elapsed / 1000);
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };
    
    // Team statistics
    this.teamStats = TEAM_CONFIG.TEAMS.map(team => ({
      id: team.id,
      name: team.name,
      color: team.color,
      units: 0,
      kills: 0
    }));
    
    // Renderer setup
    this.currentRendererType = localStorage.getItem('soulforge_rendererType') || 'canvas2d';
    
    // Select the appropriate canvas
    if (this.currentRendererType === 'threejs') {
      this.canvas = this.canvas3D;
      this.canvas2D.style.display = 'none';
      this.canvas3D.style.display = 'block';
    }
    
    this.renderer = RendererFactory.create(this.currentRendererType, this.canvas);
    
    // Force initial resize to ensure proper dimensions
    this.handleResize();
    
    // Systems
    this.inputManager = new InputManager(
      this.canvas, 
      (x, y, spawnType) => this.handleSpawn(x, y, spawnType),
      () => this.toggleRenderer(),
      () => this.togglePause()
    );
    this.uiManager = new UIManager();
    
    // Initialize StatsWindow and connect it to UIManager
    window.statsWindow = statsWindow;
    statsWindow.init();
    this.uiManager.setStatsWindow(statsWindow);
    
    // Initialize VersionNotesWindow
    window.versionNotesWindow = versionNotesWindow;
    versionNotesWindow.init();
    
    this.collisionSystem = new CollisionSystem(
      (killerTeamId, victimTeamId) => this.onKill(killerTeamId, victimTeamId),
      (x, y, deadSoldier) => this.onCorpseCreate(x, y, deadSoldier)
    );
    
    // Bind UI elements to input manager for touch support
    this.inputManager.bindUIElements(
      this.uiManager.getRendererDiv(),
      this.uiManager.getSpawnDiv()
    );
    
    // Handle window resize
    this.boundHandleResize = () => this.handleResize();
    window.addEventListener('resize', this.boundHandleResize);

    this.lastResourceSpawn = Date.now();
    this.lastNebulaSpawn = Date.now() - RESOURCE_CONFIG.NEBULA_SPAWN_INTERVAL; // Force immediate spawn
    this.lastCrystalSpawn = Date.now() - RESOURCE_CONFIG.CRYSTAL_SPAWN_INTERVAL; // Force immediate crystal spawn for testing
    
    // Initialize bases and UI
    this.createBases();
    this.updateStats();
    
    // Set version number
    const versionElement = document.getElementById('versionNumber');
    if (versionElement) {
      versionElement.textContent = VERSION;
    }
    
    // Set initial renderer type display
    this.uiManager.updateRenderer(this.currentRendererType);
    
    // Spawn initial units for all teams
    // this.spawnInitialUnits(); // Commented out - units now spawn from bases using organics
    
    // Set up browser event handlers
    this.setupBrowserEventHandlers();
    
    // Start game loop
    requestAnimationFrame((timestamp) => this.gameLoop(timestamp));
    
    // Make UIManager accessible with development commands
    window.ui = {
      showAll: () => this.uiManager.showAllUI(),
      hideOverlays: () => this.uiManager.hideAllOverlays(),
      toggleFPS: () => this.uiManager.toggleFPS(),
      testWin: (team) => this.uiManager.testWinScreen(team),
      testPause: (ms) => this.uiManager.testPause(ms),
      testStats: () => this.uiManager.testStats(),
      state: () => this.uiManager.getUIState(),
      list: () => this.uiManager.listElements(),
      cycleSpawn: () => this.uiManager.cycleSpawnType(),
      cycleRenderer: () => this.uiManager.cycleRenderer()
    };
    
    console.log('🎮 UI Manager Dev Commands:');
    console.log('  ui.showAll()        - Show all hidden UI');
    console.log('  ui.hideOverlays()   - Hide overlays');
    console.log('  ui.toggleFPS()      - Toggle FPS display');
    console.log('  ui.testWin("RED")   - Test win screen');
    console.log('  ui.testPause(3000)  - Test pause display');
    console.log('  ui.testStats()      - Test stat updates');
    console.log('  ui.state()          - Get UI state');
    console.log('  ui.list()           - List all UI elements');
    console.log('  ui.cycleSpawn()     - Cycle spawn types');
    console.log('  ui.cycleRenderer()  - Toggle 2D/3D display');
    
    // Restore window z-order after all windows are initialized
    // Small delay to ensure ConfigWindow singleton is also ready
    setTimeout(() => {
      if (typeof BaseWindow !== 'undefined' && BaseWindow.restoreZOrder) {
        BaseWindow.restoreZOrder();
        console.log('Window z-order restored');
      }
    }, 100);
  }

  canSpawnUnit(teamId = null) {
    // Check global limit
    const totalUnits = this.soldiers.filter(s => s.alive && s.health > 0).length;
    if (totalUnits >= GAME_CONFIG.MAX_TOTAL_UNITS) {
      return false;
    }
    
    // Check team limit if specified
    if (teamId !== null) {
      const teamUnits = this.soldiers.filter(s => s.alive && s.health > 0 && s.team.id === teamId).length;
      if (teamUnits >= GAME_CONFIG.MAX_UNITS_PER_TEAM) {
        return false;
      }
    }
    
    return true;
  }

  handleSpawn(x, y, spawnType) {
    if (!this.running) return;
    
    if (spawnType.type === 'soldier') {
      // Spawn soldier for specific team
      const team = spawnType.team;
      
      // Check unit limits
      if (!this.canSpawnUnit(team.id)) {
        console.log(`Cannot spawn: Unit limit reached for ${team.name} team`);
        return;
      }
      
      const base = this.bases.find(b => b.team.id === team.id);
      this.soldiers.push(new Soldier(x, y, this.canvas, team, base));
      audioManager.playSpawn();
      
      // Update team unit count
      this.teamStats[team.id].units++;
      this.updateStats();
    } else {
      // Spawn resource
      let resource;
      switch(spawnType.type) {
        case 'metal':
          resource = new Metal(x, y);
          break;
        case 'crystal':
          resource = new Crystal(x, y);
          break;
        case 'nebula':
          resource = new Nebula(x, y);
          break;
      }
      
      if (resource) {
        this.resources.push(resource);
      }
    }
  }

  updateStats() {
    const activeSoldiers = this.soldiers.filter(s => s.health > 0).length;
    const nextTeam = TEAM_CONFIG.TEAMS[this.currentTeam];
    
    // Update live unit counts
    this.teamStats.forEach(teamStat => {
      teamStat.units = this.soldiers.filter(s => s.health > 0 && s.team.id === teamStat.id).length;
    });
    
    this.uiManager.updateSoldierCount(activeSoldiers, GAME_CONFIG.MAX_TOTAL_UNITS);
    this.uiManager.updateCurrentTeam(nextTeam);
    this.uiManager.updateTeamStats(this.teamStats);
    this.uiManager.updateGameTimer(this.gameStartTime, this.paused);
    
    // Update spawn type display
    if (this.inputManager) {
      this.uiManager.updateSpawnType(this.inputManager.getCurrentSpawnType());
    }
  }

  createBases() {
    const margin = BASE_CONFIG.SIZE + UI_CONFIG.BASE_MARGIN;
    const centerX = this.canvas.width / 2;
    const centerY = this.canvas.height / 2;
    
    // Move corner bases halfway toward center for better resource access
    const positions = [
      { x: (margin + centerX) / 2, y: (margin + centerY) / 2 },                                    // Top-left (halfway to center)
      { x: (this.canvas.width - margin + centerX) / 2, y: (margin + centerY) / 2 },               // Top-right (halfway to center)
      { x: (margin + centerX) / 2, y: (this.canvas.height - margin + centerY) / 2 },              // Bottom-left (halfway to center)
      { x: (this.canvas.width - margin + centerX) / 2, y: (this.canvas.height - margin + centerY) / 2 }, // Bottom-right (halfway to center)
      { x: centerX, y: centerY }                                                                   // Center (unchanged)
    ];

    TEAM_CONFIG.TEAMS.forEach((team, idx) => {
      const pos = positions[idx] || positions[positions.length - 1];
      this.bases.push(new TeamBase(pos.x, pos.y, team, this.canvas));
    });
  }

  spawnResource(type = 'metal') {
    const margin = type === 'nebula' ? CANVAS_CONFIG.NEBULA_SPAWN_MARGIN : CANVAS_CONFIG.RESOURCE_SPAWN_MARGIN;
    
    // Try to find a spawn position away from bases
    let x, y;
    let attempts = 0;
    const maxAttempts = 50;
    const minDistanceFromBase = 100; // Minimum distance from any base
    
    do {
      x = margin + Math.random() * (this.canvas.width - margin * 2);
      y = margin + Math.random() * (this.canvas.height - margin * 2);
      
      // Check distance from all bases
      let tooClose = false;
      for (const base of this.bases) {
        const dx = x - base.x;
        const dy = y - base.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < base.size + minDistanceFromBase) {
          tooClose = true;
          break;
        }
      }
      
      if (!tooClose) break;
      attempts++;
    } while (attempts < maxAttempts);
    
    let resource;
    switch(type) {
      case 'metal':
        resource = new Metal(x, y);
        break;
      case 'nebula':
        resource = new Nebula(x, y);
        break;
      case 'crystal':
        resource = new Crystal(x, y);
        break;
      default:
        console.warn(`Unknown resource type: ${type}`);
        return;
    }
    
    this.resources.push(resource);
  }

  spawnInitialUnits() {
    // Spawn initial units for each team at random locations
    TEAM_CONFIG.TEAMS.forEach(team => {
      for (let i = 0; i < GAME_CONFIG.INITIAL_UNITS_PER_TEAM; i++) {
        // Generate random position with margin from edges
        const x = GAME_CONFIG.SPAWN_MARGIN + Math.random() * (this.canvas.width - 2 * GAME_CONFIG.SPAWN_MARGIN);
        const y = GAME_CONFIG.SPAWN_MARGIN + Math.random() * (this.canvas.height - 2 * GAME_CONFIG.SPAWN_MARGIN);
        
        // Create soldier linked to its base
        const base = this.bases.find(b => b.team.id === team.id);
        const soldier = new Soldier(x, y, this.canvas, team, base);
        
        // Make the first soldier of each team level 2
        if (i === 0) {
          soldier.levelUp(); // Promote to level 2
        }
        
        this.soldiers.push(soldier);
        
        // Update team unit count
        this.teamStats[team.id].units++;
      }
    });
    
    // Update UI after spawning all initial units
    this.updateStats();
  }

  onKill(killerTeamId, victimTeamId) {
    // Update kill count for killer's team
    const killerTeam = this.teamStats.find(team => team.id === killerTeamId);
    if (killerTeam) {
      killerTeam.kills++;
    }
  }

  onCorpseCreate(x, y, deadSoldier) {
    // Create a corpse resource at the death location
    const corpse = new Corpse(x, y, deadSoldier.team, deadSoldier.level);
    this.resources.push(corpse);
  }

  updateSoldiers() {
    // First pass: update all living soldiers
    for (let soldier of this.soldiers) {
      if (soldier.alive && soldier.health > 0) {
        soldier.update(this.soldiers, this.projectiles, this.resources, this.bases);
      }
    }

    // Revive a destroyed base if a teammate hits max level
    TEAM_CONFIG.TEAMS.forEach(team => {
      const base = this.bases.find(b => b.team.id === team.id);
      if (base && !base.alive) {
        const hero = this.soldiers.find(s => s.team.id === team.id && s.level >= GAME_CONFIG.MAX_LEVEL);
        if (hero) {
          base.x = hero.x;
          base.y = hero.y;
          base.health = base.maxHealth;
          base.alive = true;
          audioManager.playBaseSpawn();
          base.lastSpawnTime = Date.now();
          for (let s of this.soldiers) {
            if (s.team.id === team.id) {
              s.base = base;
            }
          }
        }
      }
    });

    // Second pass: remove dead soldiers and clean up references
    for (let i = this.soldiers.length - 1; i >= 0; i--) {
      const soldier = this.soldiers[i];
      if (!soldier.alive || soldier.health <= 0) {
        // Note: Nebula slot release is handled in soldier.onDeath()
        
        // Make sure no one is targeting this dead soldier
        for (let s of this.soldiers) {
          if (s.target === soldier) {
            s.clearTarget();
          }
        }
        this.soldiers.splice(i, 1);
        this.updateStats();
      }
    }
  }

  updateProjectiles() {
    // Update projectiles and remove inactive ones
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i];
      projectile.update();

      if (!projectile.active) {
        this.projectiles.splice(i, 1);
      }
    }
  }

  updateBases() {
    let spawned = false;
    for (let base of this.bases) {
      const newSoldier = base.update(this.soldiers, this.projectiles, this.resources, () => this.canSpawnUnit(base.team.id));
      if (newSoldier) {
        this.soldiers.push(newSoldier);
        this.teamStats[base.team.id].units++;
        audioManager.playSpawn();
        spawned = true;
      }
    }
    if (spawned) {
      this.updateStats();
    }
  }


  checkWinCondition() {
    const activeBases = this.bases.filter(b => b.alive);
    const activeSoldiers = this.soldiers.filter(s => s.alive && s.health > 0);
    const remainingTeams = new Set();
    activeBases.forEach(b => remainingTeams.add(b.team.id));
    activeSoldiers.forEach(s => remainingTeams.add(s.team.id));

    if (remainingTeams.size === 1 && this.running) {
      const winnerId = [...remainingTeams][0];
      const winnerTeam = TEAM_CONFIG.TEAMS.find(t => t.id === winnerId);
      this.running = false;
      
      // End all incarnations with victory/defeat status
      this.endAllIncarnations(winnerId);
      
      // Pause the game to stop units from moving
      this.isPaused = true;
      
      if (this.inputManager) {
        this.inputManager.destroy();
      }

      // Get forge session info from SoulforgeAPI
      import('./systems/SoulforgeAPI.js').then(({ soulforgeAPI }) => {
        const forgeSessionInfo = {
          sessionId: soulforgeAPI.forgeSessionId,
          id: soulforgeAPI.forgeSessionDbId
        };
        this.uiManager.showWinScreen(winnerTeam, this.teamStats, () => window.location.reload(), forgeSessionInfo);
      });

      return true;
    }
    return false;
  }
  
  endAllIncarnations(winningTeamId) {
    // Import SoulforgeAPI at runtime to avoid circular dependency
    import('./systems/SoulforgeAPI.js').then(({ soulforgeAPI }) => {
      // Send one final heartbeat with victory state before stopping
      // This ensures the backend sees the final game state
      soulforgeAPI.sendHeartbeat().then(() => {
        // Now stop the heartbeat
        soulforgeAPI.stopHeartbeat();
      });
      
      // Collect all end incarnation promises
      const endPromises = [];
      
      // End incarnations for all surviving soldiers
      this.soldiers.forEach(soldier => {
        if (soldier.alive && soldier.incarnationId) {
          const outcome = soldier.team.id === winningTeamId ? 'victory' : 'defeat';
          const promise = soulforgeAPI.endIncarnation(soldier.id, {
            outcome: outcome,
            final_level: soldier.level,
            kills: soldier.kills,
            lifetime: Date.now() - soldier.spawnTime,
            last_position: { x: soldier.x, y: soldier.y },
            last_state: soldier.state,
            game_ended: true
          });
          endPromises.push(promise);
        }
      });
      
      // Also end incarnations for any bases (future feature)
      this.bases.forEach(base => {
        if (base.alive && base.incarnationId) {
          const outcome = base.team.id === winningTeamId ? 'victory' : 'defeat';
          const promise = soulforgeAPI.endIncarnation(base.id, {
            outcome: outcome,
            game_ended: true
          });
          endPromises.push(promise);
        }
      });
      
      // Wait for all incarnations to end
      Promise.all(endPromises).then(() => {
        debugLog('[Game] All incarnations ended successfully');
      }).catch(error => {
        console.error('Error ending incarnations:', error);
      });
    });
  }
  
  setupBrowserEventHandlers() {
    // Handle page unload (refresh, close, navigate away)
    window.addEventListener('beforeunload', (event) => {
      if (this.running && window.soulforgeAPI) {
        // Send any pending events immediately
        window.soulforgeAPI.sendEventBatch();
        
        // Note: We can't make async calls reliably in beforeunload
        // The cleanup job will handle these orphaned incarnations
        console.log('[Game] Page unloading - events flushed');
      }
    });
    
    // Handle tab visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && window.soulforgeAPI) {
        // Tab is hidden - flush events
        window.soulforgeAPI.sendEventBatch();
        
        // Could implement pause/resume here in the future
        console.log('[Game] Tab hidden - events flushed');
      } else if (!document.hidden) {
        // Tab is visible again
        console.log('[Game] Tab visible');
      }
    });
    
    // Handle window focus/blur for additional safety
    window.addEventListener('blur', () => {
      if (window.soulforgeAPI) {
        window.soulforgeAPI.sendEventBatch();
      }
    });
  }
  updateResources() {
    const now = Date.now();
    
    // Spawn metal resources
    if (now - this.lastResourceSpawn > RESOURCE_CONFIG.SPAWN_INTERVAL) {
      const activeMetal = this.resources.filter(r => r.active && r instanceof Metal).length;
      if (activeMetal < RESOURCE_CONFIG.MAX_METAL_COUNT) {
        this.spawnResource('metal');
      }
      this.lastResourceSpawn = now;
    }
    
    // Spawn nebula organics
    if (now - this.lastNebulaSpawn > RESOURCE_CONFIG.NEBULA_SPAWN_INTERVAL) {
      const activeNebulas = this.resources.filter(r => r.active && r instanceof Nebula).length;
      if (activeNebulas < RESOURCE_CONFIG.MAX_NEBULA_COUNT) {
        this.spawnResource('nebula');
      }
      this.lastNebulaSpawn = now;
    }
    
    // Spawn crystal cores
    if (now - this.lastCrystalSpawn > RESOURCE_CONFIG.CRYSTAL_SPAWN_INTERVAL) {
      const activeCrystals = this.resources.filter(r => r.active && r instanceof Crystal).length;
      if (activeCrystals < RESOURCE_CONFIG.MAX_CRYSTAL_COUNT) {
        this.spawnResource('crystal');
      }
      this.lastCrystalSpawn = now;
    }
    
    // Update all resources
    for (let i = this.resources.length - 1; i >= 0; i--) {
      const res = this.resources[i];
      if (!res.active) {
        this.resources.splice(i, 1);
      } else {
        res.update();
      }
    }
  }

  async gameLoop(timestamp) {
    if (!this.running) return;

    // Update FPS counter (always update even when paused)
    if (timestamp) {
      this.uiManager.updateFPS(timestamp);
    }

    // Only update game systems when not paused
    if (!this.paused) {
      // Update game systems
      this.updateSoldiers();
      this.updateBases();
      this.updateProjectiles();
      this.updateResources();
      this.collisionSystem.update(this.projectiles, this.soldiers, [], this.bases);
    }

    // Always render (to show paused state)
    const debugStats = this.renderer.render(this.soldiers, this.projectiles, [], this.bases, this.resources);
    
    // Update UI with debug info if available
    if (debugStats) {
      this.uiManager.updateDebugInfo(debugStats);
    }

    // Check for win condition before next frame
    if (!this.checkWinCondition()) {
      requestAnimationFrame((timestamp) => this.gameLoop(timestamp));
    }
  }

  toggleRenderer() {
    // Toggle between renderer types
    this.currentRendererType = this.currentRendererType === 'canvas2d' ? 'threejs' : 'canvas2d';
    
    // Save preference
    localStorage.setItem('soulforge_rendererType', this.currentRendererType);
    
    // Switch canvas and visibility
    if (this.currentRendererType === 'threejs') {
      this.canvas = this.canvas3D;
      this.canvas2D.style.display = 'none';
      this.canvas3D.style.display = 'block';
    } else {
      this.canvas = this.canvas2D;
      this.canvas2D.style.display = 'block';
      this.canvas3D.style.display = 'none';
    }
    
    // Update input manager to use new canvas
    this.inputManager.canvas = this.canvas;
    
    // Create new renderer
    const oldRenderer = this.renderer;
    this.renderer = RendererFactory.create(this.currentRendererType, this.canvas);
    
    // Resize to current dimensions
    this.renderer.resize(this.canvas.width, this.canvas.height);
    
    // Show notification
    const rendererName = this.currentRendererType === 'threejs' ? '3D' : '2D';
    console.log(`Switched to ${rendererName} renderer`);
    
    // Update UI indicator
    this.uiManager.updateRenderer(this.currentRendererType);
  }
  
  togglePause() {
    this.paused = !this.paused;
    console.log(`Game ${this.paused ? 'PAUSED' : 'RESUMED'}`);
    
    // Update UI to show paused state
    this.uiManager.updatePausedState(this.paused);
  }
  
  handleResize() {
    // Use visualViewport for more accurate dimensions on mobile devices
    const width = window.visualViewport ? window.visualViewport.width : window.innerWidth;
    const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    
    // Resize both canvases
    this.canvas2D.width = this.canvas3D.width = width;
    this.canvas2D.height = this.canvas3D.height = height;
    
    if (this.renderer) {
      this.renderer.resize(width, height);
    }
  }
  
  destroy() {
    // Clean up systems
    if (this.inputManager) {
      this.inputManager.destroy();
    }
    
    // Clean up event listeners
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize);
    }
  }
  
  // === DEVELOPMENT COMMANDS ===
  
  // Clear localStorage for testing
  clearStorage() {
    localStorage.clear();
    console.log('🧹 localStorage cleared - reload page to see changes');
    console.log('💡 You should see the PWA install prompt on reload');
    return 'Storage cleared - reload page';
  }
  
  // Force a win for testing
  forceWin(teamName = 'RED') {
    const team = TEAM_CONFIG.TEAMS.find(t => t.name.toUpperCase() === teamName.toUpperCase());
    if (!team) {
      console.error('Invalid team name');
      return;
    }
    
    // Kill all other teams
    this.soldiers.forEach(soldier => {
      if (soldier.team.id !== team.id) {
        soldier.health = 0;
        soldier.alive = false;
      }
    });
    
    this.bases.forEach(base => {
      if (base.team.id !== team.id) {
        base.health = 0;
        base.alive = false;
      }
    });
    
    // Update stats to reflect the changes
    this.updateStats();
    
    // Force check win condition
    this.checkWinCondition();
    
    console.log(`🏆 Forced win for ${team.name} team`);
  }
  
  // Spawn units for testing
  spawnTestUnits(count = 5, teamName = null) {
    const teams = teamName ? 
      [TEAM_CONFIG.TEAMS.find(t => t.name.toUpperCase() === teamName.toUpperCase())] :
      TEAM_CONFIG.TEAMS;
    
    teams.forEach(team => {
      if (!team) return;
      
      const base = this.bases.find(b => b.team.id === team.id);
      for (let i = 0; i < count; i++) {
        const x = base.x + (Math.random() - 0.5) * 200;
        const y = base.y + (Math.random() - 0.5) * 200;
        
        if (this.canSpawnUnit(team.id)) {
          const soldier = new Soldier(x, y, this.canvas, team, base);
          soldier.levelUp(); // Start at level 2
          this.soldiers.push(soldier);
          this.teamStats[team.id].units++;
        }
      }
    });
    
    this.updateStats();
    console.log(`🤖 Spawned ${count} test units per team`);
  }
  
  // Clear all entities
  clearAll() {
    this.soldiers = [];
    this.projectiles = [];
    this.resources = [];
    
    // Reset team stats
    this.teamStats.forEach(stat => {
      stat.units = 0;
      stat.kills = 0;
    });
    
    this.updateStats();
    console.log('🧹 Cleared all entities');
  }
  
  // Max out a soldier
  maxOutSoldier(index) {
    const soldier = this.soldiers[index];
    if (!soldier) {
      console.error('Invalid soldier index');
      return;
    }
    
    // Max level
    soldier.experience = 0;
    for (let i = soldier.level; i < 15; i++) {
      soldier.levelUp();
    }
    
    // Full health and ammo
    soldier.health = soldier.maxHealth;
    soldier.ammo = soldier.maxAmmo;
    soldier.courage = soldier.maxCourage;
    
    console.log(`💪 Maxed out soldier ${index} (${soldier.team.name})`);
  }
  
  // Toggle game speed
  setGameSpeed(multiplier = 1) {
    window.gameSpeedMultiplier = multiplier;
    console.log(`⏱️ Game speed set to ${multiplier}x`);
  }
}

// Initialize game when DOM is loaded
document.addEventListener('DOMContentLoaded', async () => {
  // Preload Three.js renderer to avoid lag on first toggle
  await RendererFactory.preloadThreeJS();
  
  // Initialize PWA installer helper
  window.pwaInstaller = new PWAInstaller();
  
  // Small delay to ensure viewport dimensions are settled (important for iPads)
  setTimeout(() => {
    window.game = new Game();
    console.log('Soulforge initialized - v' + VERSION);
    // console.warn('Debug mode available - Press D or 3-finger tap');
    // console.log('Mobile log window available in debug console tab');
    
    // Add game development commands
    window.g = {
      forceWin: (team) => window.game.forceWin(team),
      spawn: (count, team) => window.game.spawnTestUnits(count, team),
      clear: () => window.game.clearAll(),
      maxOut: (index) => window.game.maxOutSoldier(index),
      speed: (mult) => window.game.setGameSpeed(mult),
      pause: () => window.game.togglePause(),
      clearStorage: () => window.game.clearStorage()
    };
    
    console.log('🎮 Game Dev Commands:');
    console.log('  g.forceWin("RED")   - Force team win');
    console.log('  g.spawn(5, "BLUE")  - Spawn test units');
    console.log('  g.clear()           - Clear all entities');
    console.log('  g.maxOut(0)         - Max out soldier');
    console.log('  g.speed(2)          - Set game speed');
    console.log('  g.pause()           - Toggle pause');
    console.log('  g.clearStorage()    - Clear localStorage');
  }, 100);
  
  // Listen for debug mode toggle from checkbox
  window.addEventListener('debugModeToggle', (e) => {
    setDebugMode(e.detail);
  });
  
  // Initialize debug mode if it was enabled
  const debugModeEnabled = localStorage.getItem('soulforge_debugMode') === 'true';
  if (debugModeEnabled) {
    setTimeout(() => {
      setDebugMode(true);
    }, 200); // Small delay to ensure DOM is ready
  }
  
  // Initialize configuration window
  configWindow.init();
});

// Console debugging commands
window.logGameState = function() {
  if (!window.game) {
    console.log('Game not initialized');
    return;
  }
  
  const game = window.game;
  
  const gameTimeSeconds = Math.floor((Date.now() - game.gameStartTime) / 1000);
  const minutes = Math.floor(gameTimeSeconds / 60);
  const seconds = gameTimeSeconds % 60;
  const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  
  console.group(`🎮 Game State (${timeStr})`);
  console.log('Running:', game.running);
  console.log('Current Team:', game.currentTeam);
  
  console.group('📊 Team Stats');
  game.teamStats.forEach(team => {
    console.log(`${team.name}: ${team.units} units, ${team.kills} kills`);
  });
  console.groupEnd();
  
  console.group('🤖 Soldiers (' + game.soldiers.length + ')');
  game.soldiers.forEach((soldier, i) => {
    if (soldier.alive && soldier.health > 0) {
      console.log(`${i}: L${soldier.level} ${soldier.team.name} - ${soldier.state} - HP:${Math.round(soldier.health)}/${Math.round(soldier.maxHealth)} - Ammo:${soldier.ammo}/${soldier.maxAmmo} - Carrying:${soldier.carryingResource ? soldier.carryingResource.type : 'none'}`);
    }
  });
  console.groupEnd();
  
  console.group('🏭 Bases (' + game.bases.length + ')');
  game.bases.forEach((base, i) => {
    console.log(`${i}: ${base.team.name} - Alive:${base.alive} - HP:${Math.round(base.health)}/${Math.round(base.maxHealth)} - Ammo:${Math.round(base.ammo)}/${base.maxAmmo} - Priority:${base.priority}`);
  });
  console.groupEnd();
  
  console.group('📦 Resources (' + game.resources.length + ')');
  let metalCount = 0, corpseCount = 0, nebulaCount = 0, crystalCount = 0;
  game.resources.forEach(res => {
    if (res.active) {
      if (res instanceof Metal) metalCount++;
      else if (res instanceof Corpse) corpseCount++;
      else if (res instanceof Nebula) nebulaCount++;
      else if (res instanceof Crystal) crystalCount++;
    }
  });
  console.log(`Metal: ${metalCount}, Corpses: ${corpseCount}, Nebulas: ${nebulaCount}, Crystals: ${crystalCount}`);
  console.groupEnd();
  
  console.group('💥 Projectiles');
  console.log('Active projectiles:', game.projectiles.length);
  console.groupEnd();
  
  console.group('👻 Soul Pool Status');
  const poolStatus = soulforgeAPI.getSoulPoolStatus();
  Object.entries(poolStatus).forEach(([forgeType, status]) => {
    console.log(`${forgeType}: ${status.count} souls ${status.refilling ? '(refilling)' : ''}`);
  });
  console.groupEnd();
  
  console.groupEnd();
};

window.logSoldierDetails = function(soldierIndex) {
  if (!window.game || !window.game.soldiers[soldierIndex]) {
    console.log('Invalid soldier index');
    return;
  }
  
  const soldier = window.game.soldiers[soldierIndex];
  console.group(`🤖 Soldier ${soldierIndex} Details`);
  console.log('Team:', soldier.team.name);
  console.log('Level:', soldier.level);
  console.log('State:', soldier.state);
  console.log('Health:', `${Math.round(soldier.health)}/${Math.round(soldier.maxHealth)}`);
  console.log('Ammo:', `${soldier.ammo}/${soldier.maxAmmo}`);
  console.log('Position:', `(${Math.round(soldier.x)}, ${Math.round(soldier.y)})`);
  console.log('Velocity:', `(${soldier.vx.toFixed(2)}, ${soldier.vy.toFixed(2)})`);
  console.log('Target:', soldier.target ? `${soldier.target.constructor.name} at (${Math.round(soldier.target.x)}, ${Math.round(soldier.target.y)})` : 'none');
  console.log('Carrying Resource:', soldier.carryingResource ? `${soldier.carryingResource.type} (amount: ${soldier.carryingResource.amount})` : 'none');
  console.log('Courage:', `${Math.round(soldier.courage)}/${soldier.maxCourage}`);
  console.log('Is Berserker:', soldier.isBerserker);
  console.groupEnd();
};

// Auto-logging for debugging (call with interval in seconds, 0 to stop)
window.autoLog = function(intervalSeconds = 0) {
  if (window.autoLogTimer) {
    clearInterval(window.autoLogTimer);
    window.autoLogTimer = null;
    console.log('Auto-logging stopped');
  }
  
  if (intervalSeconds > 0) {
    window.autoLogTimer = setInterval(() => {
      console.log(`🕐 Auto-log at ${new Date().toLocaleTimeString()}`);
      logGameState();
    }, intervalSeconds * 1000);
    console.log(`Auto-logging started every ${intervalSeconds} seconds`);
  }
};

// Soul pool debugging command
window.logSoulPool = function() {
  console.group('👻 Soul Pool Status');
  const poolStatus = soulforgeAPI.getSoulPoolStatus();
  Object.entries(poolStatus).forEach(([forgeType, status]) => {
    console.log(`${forgeType}: ${status.count} souls ready ${status.refilling ? '(currently refilling)' : ''}`);
  });
  console.groupEnd();
};

// Manual soul pool refill command
window.refillSoulPool = function(forgeType = 'combat') {
  console.log(`🔄 Manually refilling ${forgeType} soul pool...`);
  soulforgeAPI.refillSoulPool(forgeType);
};